import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Chip,
  IconButton,
  Collapse,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Stop as StopIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  AccessTime as TimeIcon,
  Science as ScienceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import API_BASE_URL from '../config';

function TestModeControl({ onTestModeChange, onTimeChange }) {
  const { t } = useTranslation();
  const [testModeEnabled, setTestModeEnabled] = useState(false);
  const [currentTime, setCurrentTime] = useState(null);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('05:00');
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualTime, setManualTime] = useState('');

  useEffect(() => {
    checkTestModeStatus();
  }, []);

  // Update manual time inputs when current time changes
  useEffect(() => {
    if (testModeEnabled && currentTime) {
      setManualDate(format(currentTime, 'yyyy-MM-dd'));
      setManualTime(format(currentTime, 'HH:mm'));
    }
  }, [testModeEnabled, currentTime]);

  const checkTestModeStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/test-mode/status`);
      setTestModeEnabled(response.data.enabled);
      if (response.data.enabled && response.data.simulated_time) {
        setCurrentTime(new Date(response.data.simulated_time));
        onTestModeChange?.(true);
      }
    } catch (error) {
      console.error('Failed to check test mode status:', error);
    }
  };

  const enableTestMode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/test-mode/enable`, {
        start_date: startDate,
        start_time: startTime,
      });

      setTestModeEnabled(true);
      setCurrentTime(new Date(response.data.simulated_time));
      setMessage('Test mode enabled');
      setExpanded(true);

      // Trigger immediate refresh
      onTestModeChange?.(true);
      // Small delay to ensure state updates propagate
      setTimeout(() => {
        onTimeChange?.(new Date(response.data.simulated_time));
      }, 100);
    } catch (error) {
      setMessage('Failed to enable test mode');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const disableTestMode = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/test-mode/disable`);
      setTestModeEnabled(false);
      setCurrentTime(null);
      setMessage('Test mode disabled');
      setExpanded(false);
      onTestModeChange?.(false);
      onTimeChange?.(new Date());
    } catch (error) {
      setMessage('Failed to disable test mode');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const advanceTime = async (minutes) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/test-mode/advance-time`, {
        minutes: minutes,
      });

      if (response.data.new_time) {
        setCurrentTime(new Date(response.data.new_time));
        setMessage(`Time advanced by ${minutes} minutes`);
        onTimeChange?.(new Date(response.data.new_time));
      }
    } catch (error) {
      setMessage('Failed to advance time');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const setExactTime = async () => {
    if (!manualDate || !manualTime) {
      setMessage('Please enter both date and time');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/test-mode/set-time`, {
        date: manualDate,
        time: manualTime,
      });

      if (response.data.new_time) {
        setCurrentTime(new Date(response.data.new_time));
        setMessage(`Time set to ${manualDate} ${manualTime}`);
        onTimeChange?.(new Date(response.data.new_time));
      }
    } catch (error) {
      setMessage('Failed to set time');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: testModeEnabled ? 'warning.light' : 'background.paper' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <ScienceIcon color={testModeEnabled ? 'warning' : 'disabled'} />
          <Typography variant="h6">
            {testModeEnabled ? t('dashboard.testModeActive') : t('dashboard.testModeInactive')}
          </Typography>
          {testModeEnabled && currentTime && (
            <Chip
              icon={<TimeIcon />}
              label={format(currentTime, 'yyyy-MM-dd HH:mm')}
              color="warning"
              variant="filled"
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <RemoveIcon /> : <AddIcon />}
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                checked={testModeEnabled}
                onChange={(e) => {
                  if (e.target.checked) {
                    if (!expanded) setExpanded(true);
                    enableTestMode();
                  } else {
                    disableTestMode();
                  }
                }}
                disabled={loading}
              />
            }
            label=""
          />
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box mt={3}>
          {!testModeEnabled ? (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label={t('dashboard.startDate')}
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label={t('dashboard.startTime')}
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="textSecondary">
                  {t('dashboard.setDesiredStartTime')}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('dashboard.manualTimeControl')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label={t('dashboard.date')}
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label={t('dashboard.time')}
                    type="time"
                    value={manualTime}
                    onChange={(e) => setManualTime(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ScheduleIcon />}
                    onClick={setExactTime}
                    disabled={loading}
                    fullWidth
                  >
                    {t('dashboard.setTime')}
                  </Button>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('dashboard.quickAdvance')}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    variant="outlined"
                    onClick={() => advanceTime(10)}
                    disabled={loading}
                    fullWidth
                  >
                    +10 min
                  </Button>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    variant="outlined"
                    onClick={() => advanceTime(30)}
                    disabled={loading}
                    fullWidth
                  >
                    +30 min
                  </Button>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    variant="outlined"
                    onClick={() => advanceTime(60)}
                    disabled={loading}
                    fullWidth
                  >
                    +1 hour
                  </Button>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Button
                    variant="outlined"
                    onClick={() => advanceTime(120)}
                    disabled={loading}
                    fullWidth
                  >
                    +2 hours
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={disableTestMode}
                    disabled={loading}
                    fullWidth
                  >
                    {t('dashboard.stopTest')}
                  </Button>
                </Grid>
              </Grid>

              <Box mt={2}>
                <Alert severity="info">
                  <Typography variant="body2">
                    {t('dashboard.testModeInfo')} {currentTime && format(currentTime, 'yyyy-MM-dd HH:mm')}.
                    {t('dashboard.autoRefreshDisabled')}
                  </Typography>
                </Alert>
              </Box>
            </>
          )}

          {message && (
            <Box mt={2}>
              <Alert severity="info" onClose={() => setMessage('')}>
                {message}
              </Alert>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default TestModeControl;